import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

interface MessageContentProps {
  content: string;
  role: 'user' | 'assistant' | 'system';
  agentRole?: 'product_manager' | 'tech_architect';
  isInternalDiscussion?: boolean;
}

export default function MessageContent({ content, role, agentRole, isInternalDiscussion }: MessageContentProps) {
  // User messages are plain text
  if (role === 'user') {
    return <p className="whitespace-pre-wrap">{content}</p>;
  }

  // Internal discussion messages have special styling
  if (isInternalDiscussion) {
    const roleLabel = agentRole === 'product_manager' ? '🔵 产品经理' : '🔧 技术架构师';
    const roleColor = agentRole === 'product_manager' ? 'text-blue-600' : 'text-orange-600';

    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className={`font-semibold ${roleColor}`}>{roleLabel}</span>
        </div>
        <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, rehypeSanitize]}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  // Assistant messages support Markdown
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          // Custom code block styling
          code({ node, className, children, ...props }: any) {
            const isInline = !className?.startsWith('language-');
            return !isInline ? (
              <pre className="rounded-md p-4 overflow-x-auto">
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            ) : (
              <code className="rounded px-1.5 py-0.5 font-mono text-sm" {...props}>
                {children}
              </code>
            );
          },
          // Custom link styling
          a({ node, children, ...props }: any) {
            return (
              <a
                {...props}
                className="text-primary underline hover:text-primary/80"
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            );
          },
          // Custom list styling
          ul({ node, children, ...props }: any) {
            return (
              <ul className="list-disc list-inside space-y-1 my-2" {...props}>
                {children}
              </ul>
            );
          },
          ol({ node, children, ...props }: any) {
            return (
              <ol className="list-decimal list-inside space-y-1 my-2" {...props}>
                {children}
              </ol>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
